import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { HeartHandshake, Sparkles, Scissors, Lock, Crown, Sparkle, Download, Trash2, ChevronRight, Receipt, CheckCircle2, AlertCircle, Link as LinkIcon, Copy, ScrollText, RotateCcw, Shield, UserCog, BookOpen, MapPin, Image as ImageIcon, Upload, X, AlertTriangle, Archive, FileArchive, Loader2 } from "lucide-react";
import { InfoTip } from "@/components/InfoTip";
import { TIPS } from "@/lib/infoTips";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { BillingForm, validateBilling } from "@/components/BillingForm";
import PasswordInput from "@/components/PasswordInput";
import SecurityPinSettings from "@/components/SecurityPinSettings";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const SECONDARY = [
  { key: "makeup", title: "Makeup", desc: "Schede trucco professionali", icon: Sparkles, label_pro: "Specializzazione makeup" },
  { key: "hair",   title: "Hair",   desc: "Schede capelli e acconciature", icon: Scissors, label_pro: "Specializzazione capelli" },
];

export default function Settings() {
  const { user, updateModules, refresh } = useAuth();
  const navigate = useNavigate();
  // Both PRO and AI_MANAGER unlock the dual-module flow (Makeup + Hair
  // freely combinable). Free/Basic still get the "primary specialization"
  // gate where exactly one specialist module can be active. Backend
  // enforcement lives in routers/clients.py::update_client and uses the
  // matching `plan not in ("pro", "ai_manager")` check — keep these in sync.
  const userPlan = (user?.plan || "free").toLowerCase();
  const isProOrAbove = userPlan === "pro" || userPlan === "ai_manager";
  const initialPrimary = user?.primary_module || (user?.modules?.includes("makeup") ? "makeup" : (user?.modules?.includes("hair") ? "hair" : ""));
  const [primary, setPrimary] = useState(initialPrimary);
  const [proExtra, setProExtra] = useState({
    makeup: isProOrAbove && (user?.modules || []).includes("makeup"),
    hair:   isProOrAbove && (user?.modules || []).includes("hair"),
  });
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [busyMods, setBusyMods] = useState(false);
  const [busyProfile, setBusyProfile] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  // ----- Branding (logo, brand_name, brand_role) -----
  const [brandName, setBrandName] = useState(user?.brand_name || "");
  const [brandRole, setBrandRole] = useState(user?.brand_role || "");
  // logoPreview: '' = no logo, '<data-url>' = current/new logo. Independent from
  // form state so we can show preview immediately on file pick before saving.
  const [logoPreview, setLogoPreview] = useState(user?.logo_url || "");
  // logoChange tracks 3 states: undefined (no change to send),
  //   '' (user removed logo, persist removal), or a data-URL (new logo).
  const [logoChange, setLogoChange] = useState(undefined);
  const [busyBranding, setBusyBranding] = useState(false);

  // ----- Billing (User SaaS fatturazione) -----
  const [billing, setBilling] = useState(null);
  const [busyBilling, setBusyBilling] = useState(false);
  const [billingLoaded, setBillingLoaded] = useState(false);

  // ----- Starting point (indirizzo operativo) -----
  const EMPTY_SP = { street: "", number: "", city: "", postal_code: "", province: "", kind: "salone" };
  const [startingPoint, setStartingPoint] = useState(EMPTY_SP);
  const [spGeocoded, setSpGeocoded] = useState(false);
  const [busySp, setBusySp] = useState(false);

  useEffect(() => {
    api.get("/users/me/starting-point").then((r) => {
      const sp = r.data || {};
      setStartingPoint({
        street: sp.street || "",
        number: sp.number || "",
        city: sp.city || "",
        postal_code: sp.postal_code || "",
        province: sp.province || "",
        kind: sp.kind || "salone",
      });
      setSpGeocoded(!!(sp.lat && sp.lng));
    }).catch(() => {});
  }, []);

  const saveStartingPoint = async () => {
    if (!startingPoint.street.trim() || !startingPoint.city.trim()) {
      toast.error("Via e Città sono richiesti per salvare il punto di partenza");
      return;
    }
    setBusySp(true);
    try {
      const { data } = await api.put("/users/me/starting-point", startingPoint);
      setSpGeocoded(!!data?.geocoded);
      toast.success(data?.geocoded
        ? "Punto di partenza salvato e geolocalizzato"
        : "Punto di partenza salvato (geolocalizzazione non disponibile)");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusySp(false); }
  };

  useEffect(() => {
    api.get("/users/me/billing").then((r) => {
      setBilling(r.data?.billing || {});
      setBillingLoaded(true);
    }).catch(() => setBillingLoaded(true));
  }, []);

  const billingError = validateBilling(billing);
  const billingComplete = !billingError && billingLoaded && billing && billing.type;

  // ----- Password change -----
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [busyPwd, setBusyPwd] = useState(false);

  const changePassword = async () => {
    if (!pwd.current) { toast.error("Inserisci la password attuale"); return; }
    if (pwd.next.length < 8) { toast.error("La nuova password deve avere almeno 8 caratteri"); return; }
    if (pwd.next !== pwd.confirm) { toast.error("Le due nuove password non coincidono"); return; }
    if (pwd.current === pwd.next) { toast.error("La nuova password deve essere diversa da quella attuale"); return; }
    setBusyPwd(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwd.current,
        new_password: pwd.next,
      });
      toast.success("Password aggiornata correttamente");
      setPwd({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusyPwd(false); }
  };

  // ----- Custom contract template -----
  const [contractTpl, setContractTpl] = useState({ conditions: "", payment_terms: "" });
  const [contractTplLoaded, setContractTplLoaded] = useState(false);
  const [busyContractTpl, setBusyContractTpl] = useState(false);
  useEffect(() => {
    api.get("/users/me/contract-template").then((r) => {
      setContractTpl({
        conditions: r.data?.conditions || "",
        payment_terms: r.data?.payment_terms || "",
      });
      setContractTplLoaded(true);
    }).catch(() => setContractTplLoaded(true));
  }, []);

  // Smooth-scroll to #contract-template when arriving from "Modifica contratto personalizzato"
  useEffect(() => {
    if (!contractTplLoaded) return;
    if (window.location.hash === "#contract-template") {
      requestAnimationFrame(() => {
        const el = document.getElementById("contract-template");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [contractTplLoaded]);

  const saveContractTemplate = async () => {
    setBusyContractTpl(true);
    try {
      await api.put("/users/me/contract-template", contractTpl);
      toast.success("Contratto personalizzato salvato");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusyContractTpl(false); }
  };

  const loadStandardIntoCustom = async () => {
    try {
      const { data } = await api.get("/contract/defaults");
      setContractTpl({
        conditions: data.conditions || "",
        payment_terms: data.payment_terms || "",
      });
      toast.success("Template standard caricato. Modifica e salva.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  // ----- Lead public link -----
  const [leadLink, setLeadLink] = useState(null);
  useEffect(() => {
    api.get("/users/me/lead-link").then((r) => setLeadLink(r.data)).catch(() => {});
  }, []);
  const fullLeadUrl = leadLink ? `${window.location.origin}${leadLink.path}` : "";
  const copyLeadLink = async () => {
    if (!fullLeadUrl) return;
    try {
      await navigator.clipboard.writeText(fullLeadUrl);
      toast.success("Link copiato negli appunti");
    } catch {
      toast.error("Impossibile copiare il link");
    }
  };

  // ----- Backup & Ripristino (locale, ZIP) -----
  // Tutto self-contained: nessuna dipendenza da servizi terzi. L'utente
  // scarica uno ZIP firmato (sha256 nel manifest) e può ripristinarlo
  // su qualsiasi installazione dell'app.
  const [busyBackupDownload, setBusyBackupDownload] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restorePreview, setRestorePreview] = useState(null);
  const [restoreError, setRestoreError] = useState("");
  const [restoreStep, setRestoreStep] = useState("pick"); // pick | confirm | running
  const [restoreAck1, setRestoreAck1] = useState(false);
  const [restoreAck2, setRestoreAck2] = useState(false);

  const downloadBackup = async () => {
    setBusyBackupDownload(true);
    // Heads-up toast: backup generation is async on the server (PDFs are
    // built in a background job) and a typical 50-client account takes
    // ~30-60 s wall-clock with the parallelized builder (v9.27). Each
    // request stays under a second though, so the polling pattern
    // survives any reverse-proxy timeout in prod.
    const prepToastId = toast.loading(
      "Preparazione backup in corso…",
      { duration: 600000 },
    );
    try {
      // 1) Kick off the job
      const { data: job } = await api.post("/backup/jobs");
      if (!job?.job_id) throw new Error("Risposta job invalida.");

      // 2) Poll status every 2s until done / failed (cap @ 10 min).
      //    Surfaces progress to the toast so the user sees "5/15
      //    clienti elaborati" instead of a blank spinner.
      const POLL_MS = 2000;
      const MAX_TRIES = Math.floor((10 * 60_000) / POLL_MS);
      let status = "pending";
      let tries = 0;
      let last = job;
      while (tries < MAX_TRIES) {
        await new Promise((r) => setTimeout(r, POLL_MS));
        const { data: cur } = await api.get(`/backup/jobs/${job.job_id}`);
        last = cur;
        status = cur?.status;
        // Live progress in the toast
        const done = cur?.progress_done;
        const total = cur?.progress_total;
        if (typeof done === "number" && typeof total === "number" && total > 0) {
          toast.loading(
            `Preparazione backup… ${done}/${total} clienti elaborati`,
            { id: prepToastId, duration: 600000 },
          );
        }
        if (status === "done" || status === "failed") break;
        tries += 1;
      }
      if (status === "failed") {
        throw new Error(last?.error || "Backup fallito sul server.");
      }
      if (status !== "done") {
        throw new Error("Timeout: il backup ha impiegato troppo tempo. Riprova.");
      }

      // 3) Trigger the actual download via a transient anchor element.
      // We use the api instance so axios attaches the Bearer token / cookie
      // exactly like every other authenticated request — same path the
      // older /export call took, just on the per-job url.
      const resp = await api.get(`/backup/jobs/${job.job_id}/download`, {
        responseType: "blob",
        timeout: 300000,
      });
      const blob = new Blob([resp.data], { type: "application/zip" });
      if (!blob.size || blob.size < 200) {
        throw new Error("Backup vuoto o incompleto. Riprova fra qualche secondo.");
      }
      const filename = last?.filename
        || (() => {
          const now = new Date();
          const pad = (n) => String(n).padStart(2, "0");
          return `abm-backup-${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}.zip`;
        })();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.dismiss(prepToastId);
      const sizeMB = (blob.size / (1024 * 1024)).toFixed(1);
      toast.success(`Backup scaricato (${sizeMB} MB) — controlla la cartella Download.`);
    } catch (err) {
      toast.dismiss(prepToastId);
      // axios returns the error body as a Blob when responseType=blob,
      // so we need to read it back to extract the message.
      let detail = err?.message || "errore sconosciuto";
      try {
        const blob = err?.response?.data;
        if (blob && blob.text) {
          const text = await blob.text();
          const j = JSON.parse(text);
          detail = formatApiError(j?.detail) || detail;
        } else if (err?.response?.data?.detail) {
          detail = formatApiError(err.response.data.detail) || detail;
        }
      } catch { /* ignore */ }
      // Friendly hints for the most common preview-vs-live differences.
      const status = err?.response?.status;
      let hint = "";
      if (err?.code === "ECONNABORTED" || /timeout/i.test(detail)) {
        hint = " · Connessione lenta? Riprova fra un minuto.";
      } else if (status === 401) {
        hint = " · Sessione scaduta, rifai il login.";
      } else if (status === 410) {
        hint = " · File scaduto, ripeti la procedura.";
      }
      toast.error(`Errore scaricamento backup: ${detail}${hint}`, { duration: 8000 });
    } finally {
      setBusyBackupDownload(false);
    }
  };

  const resetRestoreState = () => {
    setRestoreFile(null);
    setRestorePreview(null);
    setRestoreError("");
    setRestoreStep("pick");
    setRestoreAck1(false);
    setRestoreAck2(false);
  };

  const openRestoreModal = () => {
    resetRestoreState();
    setRestoreOpen(true);
  };

  const onPickRestoreFile = async (file) => {
    if (!file) return;
    if (!/\.zip$/i.test(file.name)) {
      setRestoreError("Seleziona un file ZIP prodotto da 'Scarica backup completo'.");
      return;
    }
    if (file.size > 200 * 1024 * 1024) {
      setRestoreError("File troppo grande (max 200 MB).");
      return;
    }
    setRestoreFile(file);
    setRestoreError("");
    // Immediately ask the backend for a preview (validates ZIP + version
    // + checksum). If valid, advance to the confirm step.
    const fd = new FormData();
    fd.append("file", file);
    fd.append("confirm", "false");
    fd.append("replace", "true");
    try {
      const { data } = await api.post("/backup/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      setRestorePreview(data?.preview || null);
      setRestoreStep("confirm");
    } catch (err) {
      const detail = formatApiError(err.response?.data?.detail) || err.message;
      setRestoreError(detail);
      setRestorePreview(null);
    }
  };

  const commitRestore = async () => {
    if (!restoreFile) return;
    if (!restoreAck1 || !restoreAck2) {
      setRestoreError("Conferma entrambe le caselle per procedere.");
      return;
    }
    setRestoreStep("running");
    setRestoreError("");
    const fd = new FormData();
    fd.append("file", restoreFile);
    fd.append("confirm", "true");
    fd.append("replace", "true");
    try {
      const { data } = await api.post("/backup/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000, // restore of large libraries
      });
      const c = data?.counts || {};
      toast.success(
        `Ripristino completato con successo · ${c.clients || 0} clienti, ${c.team || 0} operatori`
      );
      setRestoreOpen(false);
      resetRestoreState();
      // The user's data has been replaced — refresh the auth context so
      // any branding/profile fields update across the app.
      try { await refresh(); } catch { /* non-fatal */ }
    } catch (err) {
      const detail = formatApiError(err.response?.data?.detail) || err.message;
      setRestoreError(`Errore durante il ripristino: ${detail}`);
      setRestoreStep("confirm");
    }
  };

  const formatBackupDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("it-IT", { dateStyle: "long", timeStyle: "short" });
    } catch { return iso; }
  };

  const saveBilling = async () => {
    setBusyBilling(true);
    try {
      const err = validateBilling(billing);
      if (err) {
        toast.error(err);
        setBusyBilling(false);
        return;
      }
      await api.put("/users/me/billing", billing);
      toast.success("Dati di fatturazione salvati");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusyBilling(false); }
  };

  const tryToggle = (key) => {
    if (!isProOrAbove) {
      // FREE/BASIC: cliccare l'altra modalità apre upgrade modal; cliccare la stessa è no-op
      if (primary && key !== primary) {
        setUpgradeOpen(true);
        return;
      }
      // Se non c'è ancora una scelta, permetti di selezionarla come primary
      if (!primary) setPrimary(key);
      return;
    }
    setProExtra((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const switchPrimary = (key) => {
    // FREE/BASIC: cambia la specializzazione principale
    if (isProOrAbove) return;
    setPrimary(key);
  };

  const saveModules = async () => {
    setBusyMods(true);
    try {
      let modules = ["wedding"];
      let payloadPrimary = primary || undefined;
      if (isProOrAbove) {
        if (proExtra.makeup) modules.push("makeup");
        if (proExtra.hair)   modules.push("hair");
        if (!payloadPrimary) payloadPrimary = proExtra.makeup ? "makeup" : (proExtra.hair ? "hair" : "makeup");
      } else {
        if (!primary) {
          toast.error("Seleziona la tua specializzazione (Makeup o Hair)");
          setBusyMods(false);
          return;
        }
        modules.push(primary);
      }
      await updateModules(modules, payloadPrimary);
      toast.success(isProOrAbove ? "Modalità completa attiva" : `Specializzazione: ${primary === "makeup" ? "Makeup" : "Hair"}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusyMods(false); }
  };

  const saveProfile = async () => {
    setBusyProfile(true);
    try {
      await api.put("/users/profile", { name, phone });
      await refresh();
      toast.success("Profilo aggiornato");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusyProfile(false); }
  };

  // ----- Branding handlers -----
  const onPickLogo = (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      toast.error("Formato non supportato. Carica un'immagine (PNG, JPG, SVG, WEBP).");
      return;
    }
    if (file.size > 580 * 1024) {
      toast.error("Logo troppo grande (max 500 KB). Comprimilo prima del caricamento.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || "");
      setLogoPreview(dataUrl);
      setLogoChange(dataUrl);
    };
    reader.onerror = () => toast.error("Impossibile leggere il file selezionato.");
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    setLogoPreview("");
    setLogoChange(""); // explicit removal — sent to server on save
  };

  const saveBranding = async () => {
    setBusyBranding(true);
    try {
      const payload = {
        brand_name: brandName,
        brand_role: brandRole,
      };
      if (logoChange !== undefined) payload.logo_data = logoChange;
      await api.put("/users/branding", payload);
      await refresh();
      setLogoChange(undefined);
      toast.success("Branding aggiornato");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusyBranding(false); }
  };

  const goPro = () => {
    setUpgradeOpen(false);
    navigate("/app/plans");
  };

  return (
    <div className="max-w-3xl space-y-8" data-testid="settings-page">
      <div>
        <span className="label-uppercase">Configurazione</span>
        <h1 className="font-serif-heading text-4xl sm:text-5xl tracking-tight mt-2">Impostazioni</h1>
      </div>

      {/* Profilo */}
      <section className="bg-white border border-border rounded-sm p-8">
        <h2 className="font-serif-heading text-2xl">Profilo professionista</h2>
        <p className="text-sm text-neutral-500 mt-1">Questi dati appariranno nei PDF di preventivo e contratto.</p>
        <div className="mt-5 grid sm:grid-cols-2 gap-5">
          <div>
            <Label className="label-uppercase">Nome</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 rounded-sm" data-testid="settings-name-input" />
          </div>
          <div>
            <Label className="label-uppercase">Telefono</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1 rounded-sm" placeholder="+39 333 1234567" data-testid="settings-phone-input" />
          </div>
          <div>
            <Label className="label-uppercase">Email</Label>
            <div className="mt-1 h-10 flex items-center text-sm text-neutral-700" data-testid="settings-email">{user?.email}</div>
          </div>
        </div>
        <div className="mt-6 flex justify-end">
          <Button onClick={saveProfile} disabled={busyProfile} className="rounded-sm" data-testid="settings-save-profile-button">
            {busyProfile ? "Salvataggio…" : "Salva profilo"}
          </Button>
        </div>

        {/* Punto di partenza (indirizzo operativo) — dentro il blocco Profilo */}
        <div className="mt-8 pt-8 border-t border-border" data-testid="settings-starting-point">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <span className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <MapPin className="w-4 h-4 text-primary" strokeWidth={1.5} />
              </span>
              <div className="min-w-0">
                <h3 className="font-serif-heading text-xl">Punto di partenza</h3>
                <p className="text-xs text-neutral-500 mt-0.5">
                  Indirizzo operativo usato per calcolo km, analisi logistica e AI Manager.
                </p>
              </div>
            </div>
            {spGeocoded && (
              <span className="inline-flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-2 py-1">
                <CheckCircle2 className="w-3 h-3" strokeWidth={1.5} />
                Indirizzo geolocalizzato
              </span>
            )}
          </div>

          {!spGeocoded && (
            <div className="mt-4 bg-primary/5 border border-primary/20 rounded-sm px-3 py-2 text-xs text-neutral-700">
              Inserisci il tuo punto di partenza per attivare le analisi logistiche.
            </div>
          )}

          <div className="mt-5 grid sm:grid-cols-6 gap-4">
            <div className="sm:col-span-3">
              <Label className="label-uppercase">Via *</Label>
              <Input
                value={startingPoint.street}
                onChange={(e) => setStartingPoint({ ...startingPoint, street: e.target.value })}
                className="mt-1 rounded-sm"
                placeholder="Via Roma"
                data-testid="settings-sp-street"
              />
            </div>
            <div className="sm:col-span-1">
              <Label className="label-uppercase">Civico</Label>
              <Input
                value={startingPoint.number}
                onChange={(e) => setStartingPoint({ ...startingPoint, number: e.target.value })}
                className="mt-1 rounded-sm"
                placeholder="12A"
                data-testid="settings-sp-number"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="label-uppercase">Città *</Label>
              <Input
                value={startingPoint.city}
                onChange={(e) => setStartingPoint({ ...startingPoint, city: e.target.value })}
                className="mt-1 rounded-sm"
                placeholder="Milano"
                data-testid="settings-sp-city"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="label-uppercase">CAP</Label>
              <Input
                value={startingPoint.postal_code}
                onChange={(e) => setStartingPoint({ ...startingPoint, postal_code: e.target.value })}
                className="mt-1 rounded-sm"
                placeholder="20121"
                data-testid="settings-sp-postal"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="label-uppercase">Provincia</Label>
              <Input
                value={startingPoint.province}
                onChange={(e) => setStartingPoint({ ...startingPoint, province: e.target.value })}
                className="mt-1 rounded-sm"
                placeholder="MI"
                data-testid="settings-sp-province"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="label-uppercase">Tipo</Label>
              <Select
                value={startingPoint.kind}
                onValueChange={(v) => setStartingPoint({ ...startingPoint, kind: v })}
              >
                <SelectTrigger className="mt-1 rounded-sm" data-testid="settings-sp-kind">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="salone">Salone</SelectItem>
                  <SelectItem value="casa">Casa</SelectItem>
                  <SelectItem value="altro">Altro</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-5 flex justify-end">
            <Button
              onClick={saveStartingPoint}
              disabled={busySp || !startingPoint.street || !startingPoint.city}
              className="rounded-sm"
              data-testid="settings-sp-save"
            >
              {busySp ? "Salvataggio…" : "Salva punto di partenza"}
            </Button>
          </div>
        </div>
      </section>

      {/* Branding — logo, brand name and role applied to client-facing surfaces */}
      <section className="bg-white border border-border rounded-sm p-8" data-testid="settings-branding-section">
        <h2 className="font-serif-heading text-2xl flex items-center gap-2">
          <ImageIcon className="w-5 h-5 text-primary" strokeWidth={1.5} />
          Branding
          <InfoTip {...TIPS.settings_branding} data-testid="tip-settings-branding" />
        </h2>
        <p className="text-sm text-neutral-500 mt-1 leading-relaxed">
          Personalizza l'identità visiva mostrata ai tuoi clienti: pagina pubblica di richiesta preventivo,
          PDF di preventivo e contratto, email automatiche.
        </p>

        <div className="mt-6 grid sm:grid-cols-[180px,1fr] gap-6 items-start">
          {/* Logo preview + upload */}
          <div>
            <Label className="label-uppercase">Logo</Label>
            <div
              className="mt-2 w-full aspect-square rounded-sm border border-dashed border-border flex items-center justify-center bg-secondary/20 overflow-hidden"
              data-testid="branding-logo-preview"
            >
              {logoPreview ? (
                <img
                  src={logoPreview}
                  alt="Logo"
                  className="max-w-[88%] max-h-[88%] object-contain"
                />
              ) : (
                <div className="text-center px-3">
                  <ImageIcon className="w-7 h-7 mx-auto text-neutral-300" strokeWidth={1.2} />
                  <p className="text-[11px] text-neutral-400 mt-2 leading-tight">
                    Nessun logo caricato
                  </p>
                </div>
              )}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <label
                className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-sm border border-border bg-white hover:bg-secondary/40 cursor-pointer transition-colors"
                data-testid="branding-logo-upload-label"
              >
                <Upload className="w-3.5 h-3.5" strokeWidth={1.5} />
                {logoPreview ? "Sostituisci" : "Carica logo"}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml,image/webp"
                  className="hidden"
                  onChange={(e) => onPickLogo(e.target.files?.[0])}
                  data-testid="branding-logo-input"
                />
              </label>
              {logoPreview && (
                <button
                  type="button"
                  onClick={removeLogo}
                  className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-sm border border-border bg-white hover:bg-red-50 hover:border-red-200 hover:text-red-700 transition-colors"
                  data-testid="branding-logo-remove"
                >
                  <X className="w-3.5 h-3.5" strokeWidth={1.5} />
                  Rimuovi
                </button>
              )}
            </div>
            <p className="text-[11px] text-neutral-400 mt-2 leading-relaxed">
              PNG, JPG, SVG · max 500 KB · sfondo trasparente consigliato
            </p>
          </div>

          {/* Brand fields */}
          <div className="space-y-5">
            <div>
              <Label className="label-uppercase">Nome del brand</Label>
              <Input
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
                placeholder="Es. Studio Bridal Roma · Anna Make-up Atelier"
                className="mt-1 rounded-sm"
                maxLength={120}
                data-testid="branding-name-input"
              />
              <p className="text-[11px] text-neutral-400 mt-1">
                Apparirà come intestazione principale al posto del nome generico.
              </p>
            </div>
            <div>
              <Label className="label-uppercase">Ruolo professionale</Label>
              <Input
                value={brandRole}
                onChange={(e) => setBrandRole(e.target.value)}
                placeholder="Es. Bridal Hair & Makeup Specialist"
                className="mt-1 rounded-sm"
                maxLength={120}
                data-testid="branding-role-input"
              />
              <p className="text-[11px] text-neutral-400 mt-1">
                Sottotitolo elegante mostrato sotto il nome del brand.
              </p>
            </div>

            <div className="rounded-sm border border-border bg-secondary/20 p-4">
              <p className="text-[11px] tracking-[0.16em] uppercase text-neutral-500 mb-2">Anteprima</p>
              <div className="flex items-center gap-4">
                {logoPreview && (
                  <img
                    src={logoPreview}
                    alt=""
                    className="max-h-[60px] max-w-[120px] object-contain"
                  />
                )}
                <div className="min-w-0">
                  <p className="font-serif-heading text-xl text-neutral-900 truncate">
                    {brandName || user?.name || "Il Suo brand"}
                  </p>
                  {brandRole && (
                    <p className="text-[11px] tracking-[0.18em] uppercase text-primary mt-1">
                      {brandRole}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <Button
            onClick={saveBranding}
            disabled={busyBranding}
            className="rounded-sm"
            data-testid="branding-save-button"
          >
            {busyBranding ? "Salvataggio…" : "Salva branding"}
          </Button>
        </div>
      </section>

      {/* Bridal Quote Studio™ — entry card linking to the dedicated editor.
          Premium-only; non-AI-Manager plans see a locked teaser that still
          leads to the page (it shows the upgrade gate). Placed RIGHT AFTER
          Branding because both feature the operator's visual identity. */}
      <section
        className="bg-white border border-border rounded-sm p-8"
        data-testid="settings-quote-studio-section"
      >
        <div className="flex items-start gap-3">
          <span className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center shrink-0 mt-1">
            <Sparkles className="w-4 h-4 text-amber-700" strokeWidth={1.5} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-serif-heading text-2xl leading-tight">Bridal Quote Studio™</h2>
              <span className="text-[10px] font-medium tracking-[0.12em] uppercase px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800">
                AI Manager™
              </span>
            </div>
            <p className="text-sm text-neutral-600 mt-2 max-w-2xl leading-relaxed">
              Trasforma i tuoi preventivi in vere brochure di lusso.
              Personalizza copertina, presentazione, portfolio e contatti — il blocco prezzi viene inserito automaticamente dal sistema.
            </p>
            <div className="mt-4 flex items-center gap-3 flex-wrap">
              <Button
                onClick={() => navigate("/app/settings/quote-studio")}
                className="rounded-full"
                data-testid="settings-quote-studio-open"
              >
                <Sparkles className="w-4 h-4 mr-1.5" strokeWidth={1.6} />
                {(user?.plan || "").toLowerCase() === "ai_manager" ? "Apri Quote Studio" : "Anteprima Quote Studio"}
              </Button>
              {(user?.plan || "").toLowerCase() !== "ai_manager" && (
                <span className="text-xs text-neutral-500">
                  Disponibile con il piano AI Manager™
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Backup e Ripristino — sistema locale, ZIP self-contained.
          Posizionato dopo Branding (alta visibilità) come funzione di
          tutela primaria del lavoro dell'utente. */}
      <section className="bg-white border border-border rounded-sm p-8" data-testid="settings-backup-section">
        <div className="flex items-start gap-3">
          <span className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-1">
            <Archive className="w-4 h-4 text-primary" strokeWidth={1.5} />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="font-serif-heading text-2xl flex items-center gap-2">
              Backup e Ripristino
              <InfoTip {...TIPS.settings_backup} data-testid="tip-settings-backup" />
            </h2>
            <p className="text-sm text-neutral-500 mt-1 leading-relaxed">
              I tuoi dati sono sempre al sicuro. Puoi scaricare una copia completa o ripristinare
              un backup in qualsiasi momento.
            </p>
          </div>
        </div>

        <div className="mt-6 grid sm:grid-cols-[1fr,auto] gap-5 items-start">
          <ul className="text-xs text-neutral-500 space-y-1.5 leading-relaxed">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" strokeWidth={1.5} />
              Archivio ZIP con clienti, preventivi, contratti, timeline, foto prova e team
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" strokeWidth={1.5} />
              PDF generati al volo per ogni cliente
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" strokeWidth={1.5} />
              Nessuna dipendenza da servizi esterni — il file resta sul tuo dispositivo
            </li>
          </ul>
          <div className="flex flex-col sm:items-end gap-2 shrink-0">
            <Button
              onClick={downloadBackup}
              disabled={busyBackupDownload}
              className="rounded-sm w-full sm:w-auto"
              data-testid="backup-download-button"
            >
              <Download className={`w-4 h-4 mr-2 ${busyBackupDownload ? "animate-pulse" : ""}`} strokeWidth={1.5} />
              {busyBackupDownload ? "Preparazione…" : "Scarica backup completo"}
            </Button>
            <Button
              variant="outline"
              onClick={openRestoreModal}
              className="rounded-sm w-full sm:w-auto"
              data-testid="backup-restore-open-button"
            >
              <Upload className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Ripristina da backup
            </Button>
          </div>
        </div>
      </section>


      <section className="bg-white border border-border rounded-sm p-8" data-testid="settings-security-section">
        <h2 className="font-serif-heading text-2xl flex items-center gap-2">
          <Shield className="w-5 h-5 text-primary" strokeWidth={1.5} />
          Sicurezza
          <InfoTip {...TIPS.settings_security} data-testid="tip-settings-security" />
        </h2>
        <p className="text-sm text-neutral-500 mt-1">
          Cambia la tua password. Usa almeno 8 caratteri e una combinazione di lettere e numeri.
        </p>
        <div className="mt-5 grid sm:grid-cols-3 gap-5">
          <div>
            <Label className="label-uppercase">Password attuale</Label>
            <PasswordInput
              value={pwd.current}
              onChange={(e) => setPwd({ ...pwd, current: e.target.value })}
              className="mt-1 rounded-sm"
              autoComplete="current-password"
              testid="settings-pwd-current"
              toggleTestid="settings-pwd-current-toggle"
            />
          </div>
          <div>
            <Label className="label-uppercase">Nuova password</Label>
            <PasswordInput
              value={pwd.next}
              onChange={(e) => setPwd({ ...pwd, next: e.target.value })}
              className="mt-1 rounded-sm"
              autoComplete="new-password"
              testid="settings-pwd-next"
              toggleTestid="settings-pwd-next-toggle"
            />
          </div>
          <div>
            <Label className="label-uppercase">Conferma nuova</Label>
            <PasswordInput
              value={pwd.confirm}
              onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
              className="mt-1 rounded-sm"
              autoComplete="new-password"
              testid="settings-pwd-confirm"
              toggleTestid="settings-pwd-confirm-toggle"
            />
          </div>
        </div>
        {pwd.next && pwd.confirm && pwd.next !== pwd.confirm && (
          <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2">
            Le due nuove password non coincidono.
          </p>
        )}
        <div className="mt-6 flex justify-end">
          <Button
            onClick={changePassword}
            disabled={busyPwd || !pwd.current || pwd.next.length < 8 || pwd.next !== pwd.confirm}
            className="rounded-sm"
            data-testid="settings-pwd-submit"
          >
            {busyPwd ? "Aggiornamento…" : "Aggiorna password"}
          </Button>
        </div>
      </section>

      {/* PIN economico — protezione dati finanziari per receptionist/collaboratori */}
      <SecurityPinSettings />

      {/* Fatturazione SaaS — utente professionista */}
      <section className="bg-white border border-border rounded-sm p-8" data-testid="settings-billing-section">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-serif-heading text-2xl flex items-center gap-2">
              <Receipt className="w-5 h-5 text-primary" strokeWidth={1.5} />
              Dati di fatturazione
            </h2>
            <p className="text-sm text-neutral-500 mt-1">
              Necessari per emettere la fattura del tuo abbonamento.
              Devono essere completi prima di acquistare/aggiornare il piano.
            </p>
          </div>
          {billingLoaded && (
            billingComplete ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="billing-status-complete">
                <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Completi
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs bg-amber-50 text-amber-800 border border-amber-200" data-testid="billing-status-incomplete">
                <AlertCircle className="w-3.5 h-3.5" strokeWidth={1.5} /> Da completare
              </span>
            )
          )}
        </div>

        {billingLoaded && (
          <div className="mt-6">
            <BillingForm value={billing} onChange={setBilling} scope="user" />
          </div>
        )}

        {billingLoaded && billingError && billing?.type && (
          <p className="mt-4 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-3">
            {billingError}
          </p>
        )}

        <div className="mt-6 flex justify-end">
          <Button
            onClick={saveBilling}
            disabled={busyBilling || !billing?.type}
            className="rounded-sm"
            data-testid="settings-save-billing-button"
          >
            {busyBilling ? "Salvataggio…" : "Salva dati fatturazione"}
          </Button>
        </div>
      </section>

      {/* Link richiesta preventivo — versione compatta secondaria.
          Lo strumento principale è la "Lead Machine" card sulla Dashboard. */}
      <section className="bg-white border border-border rounded-sm p-6" data-testid="settings-lead-link-section">
        <div className="flex items-start gap-3 flex-wrap">
          <span className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center shrink-0">
            <LinkIcon className="w-4 h-4 text-neutral-500" strokeWidth={1.5} />
          </span>
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-base flex items-center gap-1.5">
              Link richiesta preventivo
              <InfoTip {...TIPS.settings_lead_link} data-testid="tip-settings-lead-link" />
            </h3>
            <p className="text-xs text-neutral-500 mt-0.5">
              Lo strumento principale è in <span className="font-medium text-neutral-700">Dashboard › Lead Machine</span>.
              Qui sotto solo per riferimento.
            </p>
          </div>
        </div>
        <div className="mt-3 bg-secondary/40 border border-border rounded-sm p-3 flex items-center gap-3 flex-wrap">
          <code className="flex-1 min-w-0 font-mono text-xs text-neutral-600 truncate" data-testid="lead-link-url">
            {fullLeadUrl || "Generazione link…"}
          </code>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-sm h-8"
            onClick={copyLeadLink}
            disabled={!fullLeadUrl}
            data-testid="lead-link-copy-button"
          >
            <Copy className="w-3 h-3 mr-1" strokeWidth={1.5} /> Copia
          </Button>
        </div>
      </section>

      {/* Moduli */}
      <section className="bg-white border border-border rounded-sm p-8">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-serif-heading text-2xl">Moduli attivi</h2>
            <p className="text-sm text-neutral-500 mt-2">La dashboard e le schede cliente si aggiornano dinamicamente.</p>
          </div>
          {isProOrAbove ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs bg-primary/10 text-primary border border-primary/20" data-testid="settings-pro-banner">
              <Crown className="w-3.5 h-3.5" strokeWidth={1.5} /> Modalità completa attiva
            </span>
          ) : primary ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs bg-neutral-100 text-neutral-700 border border-neutral-200" data-testid="settings-primary-banner">
              <Sparkle className="w-3.5 h-3.5" strokeWidth={1.5} /> Hai scelto: {primary === "makeup" ? "Makeup" : "Hair"}
            </span>
          ) : null}
        </div>

        <div className="mt-6 space-y-3">
          {/* Wedding sempre attivo */}
          <div className="flex items-center justify-between p-4 border border-border rounded-sm" data-testid="settings-module-wedding">
            <div className="flex items-center gap-4">
              <HeartHandshake className="w-5 h-5 text-primary" strokeWidth={1.5} />
              <div>
                <div className="font-medium">Wedding <span className="label-uppercase text-primary ml-2">Core</span></div>
                <div className="text-xs text-neutral-500">Sempre attivo</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Label className="label-uppercase">Attivo</Label>
              <Switch checked disabled />
            </div>
          </div>

          {SECONDARY.map((o) => {
            const Icon = o.icon;
            const isPrimary = primary === o.key;
            const active = isProOrAbove ? !!proExtra[o.key] : isPrimary;
            const lockedForNonPro = !isProOrAbove && primary && !isPrimary;
            return (
              <div
                key={o.key}
                className={`flex items-center justify-between p-4 border rounded-sm transition-colors ${
                  lockedForNonPro ? "border-border bg-neutral-50 opacity-80" : "border-border"
                }`}
                data-testid={`settings-module-${o.key}`}
              >
                <div className="flex items-center gap-4">
                  <Icon className={`w-5 h-5 ${lockedForNonPro ? "text-neutral-400" : "text-primary"}`} strokeWidth={1.5} />
                  <div>
                    <div className="font-medium flex items-center gap-2">
                      {o.title}
                      {!isProOrAbove && isPrimary && (
                        <span className="label-uppercase text-primary text-[10px]">Tua specializzazione</span>
                      )}
                      {lockedForNonPro && (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-neutral-500">
                          <Lock className="w-3 h-3" strokeWidth={2} /> Solo PRO
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-neutral-500">
                      {lockedForNonPro ? "Disponibile con piano PRO" : o.desc}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!isProOrAbove && !isPrimary && primary && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="rounded-full text-xs h-7 px-3 text-neutral-600 hover:text-primary"
                      onClick={() => setUpgradeOpen(true)}
                      data-testid={`settings-upgrade-cta-${o.key}`}
                    >
                      Sblocca
                    </Button>
                  )}
                  {!isProOrAbove && !primary && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-full text-xs h-7 px-3"
                      onClick={() => switchPrimary(o.key)}
                      data-testid={`settings-set-primary-${o.key}`}
                    >
                      Imposta come specializzazione
                    </Button>
                  )}
                  {!isProOrAbove && isPrimary && (
                    <Label className="label-uppercase">Attivo</Label>
                  )}
                  <Switch
                    checked={active}
                    onCheckedChange={() => tryToggle(o.key)}
                    disabled={!isProOrAbove && !isPrimary && !!primary}
                    data-testid={`settings-toggle-${o.key}`}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {!isProOrAbove && primary && (
          <p className="mt-4 text-xs text-neutral-500">
            Per usare {primary === "makeup" ? "anche Hair" : "anche Makeup"} contemporaneamente passa al piano <strong>PRO</strong>.
          </p>
        )}

        <div className="mt-6 flex justify-between flex-wrap gap-3">
          {!isProOrAbove && primary && (
            <Button
              variant="ghost"
              size="sm"
              className="text-neutral-600 hover:text-primary"
              onClick={() => switchPrimary(primary === "makeup" ? "hair" : "makeup")}
              data-testid="settings-switch-primary"
            >
              Cambia specializzazione → {primary === "makeup" ? "Hair" : "Makeup"}
            </Button>
          )}
          <Button onClick={saveModules} disabled={busyMods} className="rounded-sm ml-auto" data-testid="settings-save-button">
            {busyMods ? "Salvataggio…" : "Salva moduli"}
          </Button>
        </div>
      </section>

      {/* Contratto personalizzato */}
      <section
        id="contract-template"
        className="bg-white border border-border rounded-sm p-8"
        data-testid="settings-contract-template-section"
      >
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-serif-heading text-2xl flex items-center gap-2">
              <ScrollText className="w-5 h-5 text-primary" strokeWidth={1.5} />
              Contratto personalizzato
            </h2>
            <p className="text-sm text-neutral-500 mt-1">
              Modello applicato quando, in una scheda cliente, scegli "Personalizzato" come tipo contratto.
              Lascia vuoto per usare automaticamente il template standard.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-sm"
            onClick={loadStandardIntoCustom}
            data-testid="settings-load-standard-template"
          >
            <RotateCcw className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} /> Carica template standard
          </Button>
        </div>

        <div className="mt-6 space-y-5">
          <div>
            <Label className="label-uppercase">Condizioni generali</Label>
            <Textarea
              value={contractTpl.conditions}
              onChange={(e) => setContractTpl({ ...contractTpl, conditions: e.target.value })}
              className="mt-1 rounded-sm font-mono text-xs"
              rows={10}
              placeholder="Inserisci le tue condizioni generali personalizzate…"
              data-testid="settings-contract-conditions-textarea"
            />
          </div>
          <div>
            <Label className="label-uppercase">Termini di pagamento</Label>
            <Textarea
              value={contractTpl.payment_terms}
              onChange={(e) => setContractTpl({ ...contractTpl, payment_terms: e.target.value })}
              className="mt-1 rounded-sm font-mono text-xs"
              rows={6}
              placeholder="Es. 30% acconto alla firma, saldo entro 7 giorni dall'evento…"
              data-testid="settings-contract-payment-textarea"
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <Button
            onClick={saveContractTemplate}
            disabled={busyContractTpl}
            className="rounded-sm"
            data-testid="settings-save-contract-template-button"
          >
            {busyContractTpl ? "Salvataggio…" : "Salva contratto personalizzato"}
          </Button>
        </div>
      </section>

      {/* Team operatori — link alla pagina dedicata */}
      <section
        className="bg-white border border-border rounded-sm p-6"
        data-testid="settings-team-section"
      >
        <button
          type="button"
          onClick={() => navigate("/app/team")}
          className="w-full flex items-center gap-4 text-left"
          data-testid="settings-team-link"
        >
          <span className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <UserCog className="w-4 h-4 text-primary" strokeWidth={1.5} />
          </span>
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-base">Team operatori</h3>
            <p className="text-xs text-neutral-500 mt-0.5">
              Aggiungi operatori (Makeup, Hair, Entrambi) e assegna un colore per il calendario.
            </p>
          </div>
          <ChevronRight className="w-4 h-4 text-neutral-400" strokeWidth={1.5} />
        </button>
      </section>

      {/* Guida — manuale operativo */}
      <section
        className="bg-white border border-border rounded-sm p-6"
        data-testid="settings-guide-section"
      >
        <button
          type="button"
          onClick={() => navigate("/guida")}
          className="w-full flex items-center gap-4 text-left"
          data-testid="settings-guide-link"
        >
          <span className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <BookOpen className="w-4 h-4 text-primary" strokeWidth={1.5} />
          </span>
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-base">Guida all'utilizzo</h3>
            <p className="text-xs text-neutral-500 mt-0.5">
              Manuale operativo: configurazione, flusso cliente, team, calendario, best practice.
            </p>
          </div>
          <ChevronRight className="w-4 h-4 text-neutral-400" strokeWidth={1.5} />
        </button>
      </section>

      {/* Privacy e dati — link alla pagina dedicata */}
      <section className="bg-white border border-border rounded-sm p-8" data-testid="settings-privacy-section">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-serif-heading text-2xl flex items-center gap-2">
              Privacy e dati
              <InfoTip {...TIPS.settings_privacy} data-testid="tip-settings-privacy" />
            </h2>
            <p className="text-sm text-neutral-500 mt-1">
              Gestisci i tuoi diritti GDPR: scarica una copia di tutti i tuoi dati o elimina l'account.
            </p>
          </div>
        </div>
        <div className="mt-5 grid sm:grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => navigate("/app/privacy")}
            className="flex items-center justify-between gap-3 p-4 border border-border rounded-sm text-left hover:border-primary/50 hover:bg-primary/5 transition-colors group"
            data-testid="settings-privacy-export-btn"
          >
            <div className="flex items-center gap-3 min-w-0">
              <span className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                <Download className="w-4 h-4" strokeWidth={1.5} />
              </span>
              <div className="min-w-0">
                <div className="font-medium text-sm">Scarica i miei dati</div>
                <div className="text-xs text-neutral-500 truncate">Export JSON · Art. 20 GDPR</div>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-neutral-400 group-hover:text-primary transition-colors shrink-0" strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={() => navigate("/app/privacy")}
            className="flex items-center justify-between gap-3 p-4 border border-rose-200 rounded-sm text-left hover:bg-rose-50/50 transition-colors group"
            data-testid="settings-privacy-delete-btn"
          >
            <div className="flex items-center gap-3 min-w-0">
              <span className="w-9 h-9 rounded-full bg-rose-50 text-rose-700 flex items-center justify-center shrink-0">
                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
              </span>
              <div className="min-w-0">
                <div className="font-medium text-sm">Elimina account</div>
                <div className="text-xs text-neutral-500 truncate">Cancellazione totale · Art. 17 GDPR</div>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-neutral-400 group-hover:text-rose-600 transition-colors shrink-0" strokeWidth={1.5} />
          </button>
        </div>
      </section>

      {/* Restore modal — two-step flow:
          1. pick → file input (validated client+server side)
          2. confirm → preview (counts + date) + double opt-in checkboxes
          3. running → busy state, restore endpoint blocks until done
      */}
      <Dialog
        open={restoreOpen}
        onOpenChange={(open) => {
          if (!open && restoreStep !== "running") {
            setRestoreOpen(false);
            resetRestoreState();
          }
        }}
      >
        <DialogContent
          className="rounded-md max-w-lg"
          data-testid="backup-restore-modal"
          onInteractOutside={(e) => { if (restoreStep === "running") e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (restoreStep === "running") e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle className="font-serif-heading text-2xl flex items-center gap-2">
              <FileArchive className="w-5 h-5 text-primary" strokeWidth={1.5} />
              Ripristina da backup
            </DialogTitle>
            <DialogDescription>
              Carica un archivio ZIP precedentemente scaricato per ripristinare i tuoi dati.
            </DialogDescription>
          </DialogHeader>

          {restoreStep === "pick" && (
            <div className="space-y-4 py-2">
              <label
                htmlFor="restore-file-input"
                className="flex flex-col items-center justify-center w-full h-36 rounded-sm border-2 border-dashed border-border bg-secondary/20 cursor-pointer hover:bg-secondary/40 hover:border-primary/40 transition-colors"
                data-testid="backup-restore-dropzone"
              >
                <Upload className="w-7 h-7 text-neutral-400 mb-2" strokeWidth={1.5} />
                <p className="text-sm text-neutral-700">Clicca per selezionare un file ZIP</p>
                <p className="text-xs text-neutral-500 mt-1">max 200 MB · solo backup prodotti da questa app</p>
              </label>
              <input
                id="restore-file-input"
                type="file"
                accept=".zip,application/zip"
                className="hidden"
                onChange={(e) => onPickRestoreFile(e.target.files?.[0])}
                data-testid="backup-restore-file-input"
              />
              {restoreError && (
                <p className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-sm p-2" data-testid="backup-restore-error">
                  {restoreError}
                </p>
              )}
            </div>
          )}

          {restoreStep === "confirm" && restorePreview && (
            <div className="space-y-4 py-2" data-testid="backup-restore-confirm-step">
              <div className="rounded-sm border border-border bg-secondary/30 p-4 space-y-2">
                <p className="label-uppercase text-neutral-500">Anteprima archivio</p>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-xs text-neutral-500">Data backup</p>
                    <p className="font-medium text-neutral-900" data-testid="backup-restore-preview-date">
                      {formatBackupDate(restorePreview.created_at)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-neutral-500">Clienti</p>
                    <p className="font-medium text-neutral-900" data-testid="backup-restore-preview-clients">
                      {restorePreview.clients_count ?? 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-neutral-500">Operatori</p>
                    <p className="font-medium text-neutral-900">{restorePreview.team_count ?? 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-neutral-500">Eventi</p>
                    <p className="font-medium text-neutral-900">{restorePreview.events_count ?? 0}</p>
                  </div>
                </div>
                {restorePreview.source_email && (
                  <p className="text-[11px] text-neutral-500 pt-1 border-t border-border mt-2">
                    Origine: <span className="font-mono">{restorePreview.source_email}</span>
                  </p>
                )}
              </div>

              <div className="rounded-sm border border-amber-200 bg-amber-50 p-4 flex gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" strokeWidth={1.5} />
                <div className="text-sm text-amber-900 leading-relaxed">
                  <p className="font-medium">Questa operazione sostituirà i dati attuali.</p>
                  <p className="text-xs mt-1 text-amber-800">
                    Tutti i clienti, preventivi, contratti, timeline, operatori, eventi e notifiche
                    saranno cancellati e ricreati da zero a partire dal contenuto di questo file.
                    L'operazione è irreversibile.
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <label className="flex items-start gap-2 text-sm text-neutral-700 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-1 cursor-pointer"
                    checked={restoreAck1}
                    onChange={(e) => setRestoreAck1(e.target.checked)}
                    data-testid="backup-restore-ack1"
                  />
                  <span>Ho scaricato un backup recente, oppure non mi serve conservare i dati attuali.</span>
                </label>
                <label className="flex items-start gap-2 text-sm text-neutral-700 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-1 cursor-pointer"
                    checked={restoreAck2}
                    onChange={(e) => setRestoreAck2(e.target.checked)}
                    data-testid="backup-restore-ack2"
                  />
                  <span>Confermo di voler sostituire tutti i miei dati con il contenuto del file selezionato.</span>
                </label>
              </div>

              {restoreError && (
                <p className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-sm p-2" data-testid="backup-restore-error">
                  {restoreError}
                </p>
              )}
            </div>
          )}

          {restoreStep === "running" && (
            <div className="py-8 flex flex-col items-center text-center" data-testid="backup-restore-running">
              <Loader2 className="w-10 h-10 text-primary animate-spin" strokeWidth={1.5} />
              <p className="mt-4 text-sm font-medium text-neutral-900">Ripristino in corso…</p>
              <p className="text-xs text-neutral-500 mt-1">Non chiudere questa finestra né ricaricare la pagina.</p>
            </div>
          )}

          <DialogFooter className="gap-2">
            {restoreStep === "pick" && (
              <Button
                variant="outline"
                className="rounded-sm"
                onClick={() => { setRestoreOpen(false); resetRestoreState(); }}
                data-testid="backup-restore-cancel-pick"
              >
                Annulla
              </Button>
            )}
            {restoreStep === "confirm" && (
              <>
                <Button
                  variant="outline"
                  className="rounded-sm"
                  onClick={() => { setRestoreOpen(false); resetRestoreState(); }}
                  data-testid="backup-restore-cancel-confirm"
                >
                  Annulla
                </Button>
                <Button
                  className="rounded-sm bg-rose-600 hover:bg-rose-700 text-white"
                  onClick={commitRestore}
                  disabled={!restoreAck1 || !restoreAck2}
                  data-testid="backup-restore-confirm-button"
                >
                  Conferma e ripristina
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upgrade modal */}
      <Dialog open={upgradeOpen} onOpenChange={setUpgradeOpen}>
        <DialogContent className="rounded-md" data-testid="upgrade-pro-modal">
          <DialogHeader>
            <DialogTitle className="font-serif-heading text-2xl flex items-center gap-2">
              <Crown className="w-5 h-5 text-primary" strokeWidth={1.5} />
              Sblocca entrambe le modalità
            </DialogTitle>
            <DialogDescription>
              Con il piano <strong>PRO</strong> puoi usare sia <strong>Makeup</strong> che <strong>Hair</strong> insieme,
              oltre a timeline avanzata, mappa e gestione team.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-sm" onClick={() => setUpgradeOpen(false)} data-testid="upgrade-cancel">Annulla</Button>
            <Button className="rounded-sm" onClick={goPro} data-testid="upgrade-go-pro">
              <Crown className="w-4 h-4 mr-2" strokeWidth={1.5} /> Passa a PRO
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
